import { useTranslation } from "react-i18next";
import { EyeSlashIcon, FlagIcon as FlagOutline } from "@heroicons/react/24/outline";
import {
  FlagIcon as FlagSolid,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import KaTeXComponent from "../KaTeXComponent/KaTeXComponent";
import { DifficultyBadge } from "../Difficulty/Difficulty";

function OptionItem({
  option,
  isSelected,
  isReview,
  isCorrectAnswer,
  isWrongSelection,
  onClick,
  disabled,
}) {
  const { t } = useTranslation();
  let borderClass = "border-slate-200";
  let bgClass = "bg-white enabled:hover:border-blue-300 enabled:hover:bg-blue-50/40";

  if (isReview) {
    if (isCorrectAnswer) {
      borderClass = "border-emerald-500 ring-1 ring-emerald-500";
      bgClass = "bg-emerald-50";
    } else if (isWrongSelection) {
      borderClass = "border-red-500 ring-1 ring-red-500";
      bgClass = "bg-red-50";
    } else if (isSelected) {
      // The exam hides the correct answers: the student's own pick, neither right nor wrong.
      borderClass = "border-blue-600 ring-1 ring-blue-600";
      bgClass = "bg-blue-50";
    }
  } else if (isSelected) {
    borderClass = "border-blue-600 ring-1 ring-blue-600";
    bgClass = "bg-blue-50 shadow-sm shadow-blue-600/10";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isReview}
      aria-label={t("exam_taking.option_label", { option: option.option })}
      className={`group flex min-h-[56px] w-full items-start gap-4 rounded-xl border px-4 py-3.5 text-left transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${borderClass} ${bgClass} ${
        disabled || isReview ? "cursor-default" : "cursor-pointer"
      }`}
    >
      <span
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold transition duration-200 ${
          isReview && isCorrectAnswer
            ? "bg-emerald-600 text-white"
            : isReview && isWrongSelection
              ? "bg-red-600 text-white"
              : isSelected
                ? "bg-blue-600 text-white"
                : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 group-enabled:group-hover:text-blue-700 group-enabled:group-hover:ring-blue-400"
        }`}
      >
        {option.option}
      </span>
      <div className="min-w-0 flex-1 pt-1 text-base leading-relaxed text-slate-800"><KaTeXComponent text={option.body} /></div>
      <CheckCircleIcon
        aria-hidden="true"
        className={`mt-1 h-6 w-6 flex-shrink-0 transition duration-200 ${
          isReview
            ? isCorrectAnswer
              ? "text-emerald-600"
              : "hidden"
            : isSelected
              ? "text-blue-600"
              : "invisible"
        }`}
      />
      <XCircleIcon
        aria-hidden="true"
        className={`mt-1 h-6 w-6 flex-shrink-0 text-red-600 ${
          isReview && isWrongSelection ? "" : "hidden"
        }`}
      />
    </button>
  );
}

export default function QuestionDisplay({
  question,
  selectedAnswerUuid,
  onSelectAnswer,
  disabled = false,
  isReview = false,
  correctAnswerUuids = [],
  isFlagged = false,
  onToggleFlag,
  // Staff screens only (the test preview): students are not shown the question's difficulty.
  difficulty = null,
  // What a question whose answers the user may not read (question.answers_hidden) says in review: by default that of
  // another's attempt (neither the right answer nor the student's choice is shown); the caller's own words otherwise
  // (the user's own attempt, the test's preview).
  answersHiddenNote = null,
}) {
  const { t } = useTranslation();

  if (!question) return null;

  // A teacher's review of another's attempt, or a test's preview, with a question whose answers the user may not read
  // in the bank: no option is marked right or wrong, nor scored, and a quiet line says why. (What was chosen is the
  // caller's: the server leaves another's pick out; the user's own, in the review or the preview, shows as a pick.)
  const answersHidden = isReview && question.answers_hidden === true;
  const correctSet = new Set(answersHidden ? [] : correctAnswerUuids);
  // An empty list means the exam does not show correct answers: a pick can't be called wrong then.
  const answersKnown = correctSet.size > 0;
  // The API sends points as decimal strings ("1.00"); plural forms need a number.
  const points = Number(question.points);
  const pointsEarned = Number(question.points_earned) || 0;
  const pointsPossible = Number(question.points_possible);
  // null while the exam keeps per-question results hidden (attempts left, exam still open).
  const resultKnown = typeof question.is_correct === "boolean";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {/* The code finds the question (and its answer key) in the bank: the server sends it to staff only. */}
          {question.question_code && (
            <span className="inline-flex items-center truncate rounded-md bg-slate-100 px-2.5 py-1 font-mono text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
              {question.question_code}
            </span>
          )}
          {onToggleFlag && !isReview && (
            <button
              type="button"
              onClick={() => onToggleFlag(question.attempt_question_uuid)}
              aria-label={t("exam_taking.toggle_flag")}
              className={`group rounded-lg p-2 transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                isFlagged
                  ? "bg-amber-50 ring-1 ring-inset ring-amber-200 hover:bg-amber-100"
                  : "hover:bg-slate-100"
              }`}
            >
              {isFlagged ? (
                <FlagSolid className="h-5 w-5 text-amber-500" />
              ) : (
                <FlagOutline className="h-5 w-5 text-slate-400 transition duration-200 group-hover:text-amber-500" />
              )}
            </button>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <DifficultyBadge level={difficulty} withLabel showPoints={false} />
          {Number.isFinite(points) && (
            <span className="inline-flex flex-shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
              {t("exam_taking.points_value", { count: points })}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-none select-none text-base leading-relaxed text-slate-900 sm:text-lg sm:leading-8 min-[1920px]:text-xl min-[1920px]:leading-9">
        <KaTeXComponent text={question.question_body} />
      </div>

      <div className="flex select-none flex-col gap-3">
        {question.options?.map((option) => {
          const isSelected = selectedAnswerUuid === option.uuid;
          const isCorrectAnswer = isReview && correctSet.has(option.uuid);
          const isWrongSelection =
            isReview && answersKnown && isSelected && !correctSet.has(option.uuid);

          return (
            <OptionItem
              key={option.uuid}
              option={option}
              isSelected={isSelected}
              isReview={isReview}
              isCorrectAnswer={isCorrectAnswer}
              isWrongSelection={isWrongSelection}
              disabled={disabled}
              onClick={() =>
                !disabled &&
                !isReview &&
                onSelectAnswer?.(question.attempt_question_uuid, option.uuid)
              }
            />
          );
        })}
      </div>

      {answersHidden && (
        <p className="flex items-start gap-2 border-t border-slate-100 pt-4 text-sm text-slate-500" data-testid="answers-hidden-note">
          <EyeSlashIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">{answersHiddenNote || t("exam_taking.answers_hidden")}</span>
        </p>
      )}

      {isReview && !answersHidden && resultKnown && Number.isFinite(pointsPossible) && (
        <div className="flex items-center gap-4 border-t border-slate-100 pt-4 text-sm">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
              question.is_correct
                ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                : "bg-red-50 text-red-700 ring-red-600/20"
            }`}
          >
            {t("exam_taking.points_earned", {
              earned: pointsEarned,
              count: pointsPossible,
            })}
          </span>
        </div>
      )}
    </div>
  );
}
